import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const links = [
  { label: "企業理念", to: "/philosophy" },
  { label: "サービス", to: "/services" },
  { label: "開発実績", to: "/works" },
  { label: "会社概要", to: "/company" },
  { label: "採用情報", to: "/recruit" },
  { label: "お問い合わせ", to: "/contact" },
];

export const Header = () => {
  const logo = useContent().site.logo;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "bg-black/20 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 font-display text-xl font-extrabold tracking-tight text-white">
          <img src={logo} alt="PRC Remit" className="h-8 w-8 object-contain" />
          PRC<span className="text-gold"> REMIT</span>
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm transition-colors duration-300 tracking-wide ${isActive ? "text-gold" : "text-white/60 hover:text-gold"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <Link
          to="/contact"
          data-testid="header-cta"
          className="hidden md:inline-flex items-center rounded-full border border-gold/40 px-5 py-2 text-sm text-gold hover:bg-gold hover:text-ink hover:border-gold transition-colors duration-300"
        >
          ご相談
        </Link>
        <button data-testid="menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.to.slice(1)}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `text-lg transition-colors ${isActive ? "text-gold" : "text-white/80 hover:text-gold"}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      </motion.header>
  );
};
